/**
 * Manifesto — §02. Three-row "what Nyx does" with album-driven demos.
 *
 * Layout per row:  [ text  ]  [ demo ]
 *   - is-blue    : structure spine pictogram (3-pin curve, real track titles)
 *   - is-violet  : poster quote from songs[0].key_lines[0] when present
 *   - is-green   : live-prep card built from songs[0].live.*
 *
 * The pictogram in row 1 is a static decorative SVG, not the deep dive's
 * full ArchCurve component — the manifesto wants a small gesture toward
 * "this album has shape," not a second analytical visualization. Pins
 * underneath are populated from real data so the row still re-keys on
 * album swap.
 *
 * Row 3's italic cue is the first sentence of `songs[0].live.listen_for` when
 * present, trimmed via the same heuristic we use in DeepDive's `splitMovement`.
 * Keeps the card compact while staying data-driven.
 */

import { Reveal } from './primitives/Reveal';
import { manifestoCopy } from '@/copy/manifesto';
import type { AvailableAlbumModule } from '@/types/album';

interface ManifestoProps {
  album: AvailableAlbumModule;
}

function firstSentence(text: string): string {
  const match = /[.!?](?:["')\]]+)?(?=\s|$)/.exec(text);
  if (!match) return text;
  return text.slice(0, match.index + match[0].length);
}

export function Manifesto({ album }: ManifestoProps): JSX.Element {
  const { tracks } = album.deepDive;
  const hingeIdx = tracks.findIndex((t) => t.hinge);
  const fallbackIdx = Math.min(tracks.length - 1, Math.floor(tracks.length * 0.55));
  const centerTrack = tracks[hingeIdx >= 0 ? hingeIdx : fallbackIdx]!;

  const song = album.songs[0]!;
  const keyLine = song.key_lines[0];
  const cue = firstSentence(song.live.listen_for ?? song.live.mindset).trim();

  const [pillarStructure, pillarMeaning, pillarLive] = manifestoCopy.pillars;

  return (
    <section
      id="manifesto"
      className="nl-manifesto"
      aria-label="Tres formas de escuchar con Nyx"
    >
      <Reveal as="div" className="nl-mf-eyebrow">
        {manifestoCopy.eyebrow}
      </Reveal>

      {/* ROW 1 — ESTRUCTURA · spine pictogram (blue) */}
      <Reveal as="article" className={`nl-mf-row is-${pillarStructure.accent}`}>
        <div className="nl-mf-text">
          <div className="nl-mf-num">{pillarStructure.num}</div>
          <h3 className="nl-mf-title">{pillarStructure.title}</h3>
          <p className="nl-mf-body">{pillarStructure.body}</p>
        </div>
        <div className="nl-mf-demo nl-mf-spine" aria-hidden="true">
          <svg viewBox="0 0 600 90" preserveAspectRatio="none">
            <path
              d="M 10,58 C 50,58 70,50 100,38 C 130,28 160,22 190,28 C 220,34 240,52 280,56 C 320,60 350,46 380,32 C 410,20 430,30 460,46 C 490,60 520,54 590,60"
              stroke="currentColor"
              fill="none"
              strokeWidth="1.5"
              opacity="0.5"
            />
            <circle cx="190" cy="28" r="3" fill="currentColor" opacity="0.7" />
            <circle cx="380" cy="32" r="5" fill="currentColor" />
            <circle cx="380" cy="32" r="11" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.45" />
            <circle cx="380" cy="32" r="20" fill="none" stroke="currentColor" strokeWidth="0.4" opacity="0.18" />
            <circle cx="490" cy="60" r="3" fill="currentColor" opacity="0.7" />
          </svg>
          <div className="nl-mf-spine-pins">
            <span>{tracks[0]!.title}</span>
            <span className="is-key">{centerTrack.title}</span>
            <span>{tracks[tracks.length - 1]!.title}</span>
          </div>
          <div className="nl-mf-demo-cap">{`Arco del disco · ${tracks.length} pistas`}</div>
        </div>
      </Reveal>

      {/* ROW 2 — SIGNIFICADO · poster quote (violet) */}
      <Reveal as="article" className={`nl-mf-row is-${pillarMeaning.accent}`}>
        <div className="nl-mf-text">
          <div className="nl-mf-num">{pillarMeaning.num}</div>
          <h3 className="nl-mf-title">{pillarMeaning.title}</h3>
          <p className="nl-mf-body">{pillarMeaning.body}</p>
        </div>
        <div className="nl-mf-demo nl-mf-quote">
          <div className="nl-mf-quote-line">{keyLine?.lyric ?? song.live.line_to_feel}</div>
          <div className="nl-mf-quote-rule" aria-hidden="true" />
          <div className="nl-mf-quote-gloss">{keyLine?.explanation ?? song.summary}</div>
          <div className="nl-mf-demo-cap">{`${song.title} · Frase clave`}</div>
        </div>
      </Reveal>

      {/* ROW 3 — EN VIVO · live-prep card (green) */}
      <Reveal as="article" className={`nl-mf-row is-${pillarLive.accent}`}>
        <div className="nl-mf-text">
          <div className="nl-mf-num">{pillarLive.num}</div>
          <h3 className="nl-mf-title">{pillarLive.title}</h3>
          <p className="nl-mf-body">{pillarLive.body}</p>
        </div>
        <div className="nl-mf-demo nl-mf-livecard">
          <div className="nl-mf-card-eyebrow">Frase para sentir</div>
          <div className="nl-mf-card-line">{`\u201C${song.live.line_to_feel}\u201D`}</div>
          {cue ? <div className="nl-mf-card-cue">{cue}</div> : null}
          <div className="nl-mf-demo-cap">{`${song.title} · Listo para el show`}</div>
        </div>
      </Reveal>
    </section>
  );
}
